import type {
  RecallActivityItem,
  RecallCampaignRow,
  RecallChannelRow,
  RecallChartPoint,
  RecallMetrics,
  RecallRange,
  RecallRangeKey,
} from './types';
import { rcMoney, rcNum } from './utils';

export const RC_RANGES: RecallRange[] = [
  { key: 'empty', label: 'Day 1', chart: 'empty', days: 1, unlockAfter: 0 },
  { key: '7d', label: '7 days', chart: 'dailyBars', days: 7, unlockAfter: 7 },
  { key: '30d', label: '30 days', chart: 'area', days: 30, unlockAfter: 30 },
  { key: '90d', label: '3 months', chart: 'weeklyBars', days: 90, unlockAfter: 90 },
  { key: '12m', label: '12 months', chart: 'monthlyBars', days: 365, unlockAfter: 365 },
];

const RC_DAILY_SENT = 790;
const RC_CONV: Record<Exclude<RecallRangeKey, 'empty'>, number> = {
  '7d': 71,
  '30d': 73,
  '90d': 72,
  '12m': 69,
};

const RC_DELTA: Record<Exclude<RecallRangeKey, 'empty'>, RecallMetrics['delta']> = {
  '7d': { revenue: '+6.1%', sent: '+3.8%', reached: '+4.0%', conversion: '+1.2pt' },
  '30d': { revenue: '+12.4%', sent: '+8.1%', reached: '+9.3%', conversion: '+4.2pt' },
  '90d': { revenue: '+18.7%', sent: '+11.0%', reached: '+12.4%', conversion: '+6.0pt' },
  '12m': { revenue: '+34.2%', sent: '+22.5%', reached: '+19.8%', conversion: '+9.1pt' },
};

export function rcMetrics(key: RecallRangeKey): RecallMetrics | null {
  if (key === 'empty') return null;
  const days = RC_RANGES.find((r) => r.key === key)?.days ?? 30;
  const conv = RC_CONV[key];
  const sent = days * RC_DAILY_SENT;
  const reached = Math.round(sent / 5.74);
  const revenue = days * 16433;
  const d = RC_DELTA[key];
  return {
    revenue: rcMoney(revenue),
    revParts: [
      { label: 'New patient visits', amt: rcMoney(revenue * 0.38), tone: 'success' },
      { label: 'Recall + hygiene', amt: rcMoney(revenue * 0.3), tone: 'info' },
      { label: 'Reactivations', amt: rcMoney(revenue * 0.18), tone: 'gold' },
      { label: 'Collections', amt: rcMoney(revenue * 0.14), tone: 'terra' },
    ],
    revPct: [38, 30, 18, 14],
    perMessage: '$98',
    sent: rcNum(sent),
    reached: rcNum(reached),
    activeRecalls: '17',
    activeTotal: '28',
    conversion: `${conv}%`,
    noShow: '4.1%',
    delta: d,
  };
}

const RC_RECALLS_BASE: Omit<RecallCampaignRow, 'sentN'>[] = [
  { name: 'Appointment confirmation', goal: 'Confirm', sent: 12840, primary: '89%', label: 'Confirm rate', tone: 'info' },
  { name: 'Hygiene recall', goal: 'Book', sent: 5026, primary: '73%', label: 'Booking rate', tone: 'ai' },
  { name: 'Insurance utilization', goal: 'Book', sent: 1890, primary: '38%', label: 'Booking rate', tone: 'lav' },
  { name: 'Outstanding balance', goal: 'Collect', sent: 670, primary: '$18.4k', label: 'Collected', tone: 'success' },
  { name: 'Post-op follow-up', goal: 'Safety', sent: 342, primary: '87%', label: 'Response rate', tone: 'warning' },
  { name: 'Patient reactivation', goal: 'Reactivate', sent: 1420, primary: '28%', label: 'Reactivated', tone: 'gold' },
  { name: 'Treatment follow-up', goal: 'Schedule', sent: 890, primary: '34%', label: 'Scheduled', tone: 'info' },
  { name: 'New patient onboarding', goal: 'Collect info', sent: 489, primary: '84%', label: 'Forms done', tone: 'ai' },
];

export function rcRecalls(key: RecallRangeKey): RecallCampaignRow[] {
  const days = RC_RANGES.find((r) => r.key === key)?.days ?? 30;
  const ratio = days / 30;
  return RC_RECALLS_BASE.map((r) => ({ ...r, sentN: Math.round(r.sent * ratio) }));
}

const RC_CHANNELS_BASE = [
  { channel: 'Email', icon: 'mail' as const, sent: 12400, converted: 3720, tone: 'info' as const },
  { channel: 'SMS', icon: 'chat' as const, sent: 6800, converted: 2040, tone: 'gold' as const },
  { channel: 'AI Voice', icon: 'sparkle' as const, sent: 3200, converted: 960, tone: 'ai' as const },
  { channel: 'Human Call', icon: 'headphones' as const, sent: 1360, converted: 586, tone: 'success' as const },
];

export function rcChannels(key: RecallRangeKey): RecallChannelRow[] {
  const days = RC_RANGES.find((r) => r.key === key)?.days ?? 30;
  const ratio = days / 30;
  return RC_CHANNELS_BASE.map((c) => {
    const sentN = Math.round(c.sent * ratio);
    const convertedN = Math.round(c.converted * ratio);
    return {
      ...c,
      sentN,
      convertedN,
      rate: Math.round((c.converted / c.sent) * 100),
    };
  });
}

export const RC_ACTIVITY: RecallActivityItem[] = [
  { time: '2 min ago', text: 'Sarah Mitchell booked hygiene cleaning for Apr 2', tone: 'success' },
  { time: '8 min ago', text: 'AI voice call to Linda Ramirez — went to voicemail', tone: 'info' },
  { time: '12 min ago', text: 'James Harrison confirmed implant cleaning via SMS', tone: 'success' },
  { time: '25 min ago', text: 'Maria Gonzalez — pain 9/10 flagged, escalated to staff', tone: 'warning' },
  { time: '32 min ago', text: 'Tom Park paid $420 balance via email payment link', tone: 'success' },
  { time: '1h ago', text: 'Dorothy Williams — birthday SMS delivered', tone: 'info' },
  { time: '1h ago', text: 'Kevin Wright assigned to human-call queue (balance)', tone: 'gold' },
  { time: '2h ago', text: 'Jennifer Lee booked perio maintenance for Mar 27', tone: 'success' },
];

const RC_TODAY = new Date();
const RC_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const RC_D7: RecallChartPoint[] = [
  { x: 'Mon', sent: 712, rate: 70 },
  { x: 'Tue', sent: 884, rate: 72 },
  { x: 'Wed', sent: 951, rate: 74 },
  { x: 'Thu', sent: 806, rate: 71 },
  { x: 'Fri', sent: 992, rate: 76 },
  { x: 'Sat', sent: 548, rate: 69 },
  { x: 'Sun', sent: 471, rate: 68 },
].map((d) => ({ ...d, conv: Math.round((d.sent ?? 0) * (d.rate ?? 0) / 100) }));

const RC_D30: RecallChartPoint[] = Array.from({ length: 30 }, (_, i) => {
  const dt = new Date(RC_TODAY);
  dt.setDate(dt.getDate() - (29 - i));
  const wd = dt.getDay();
  const weekend = wd === 0 || wd === 6;
  const sent = Math.round((820 + 130 * Math.sin(i / 2.3)) * (weekend ? 0.6 : 1));
  const rate = 70 + i * 0.17 + 1.3 * Math.sin(i / 1.7);
  return {
    day: i + 1,
    sent,
    rate: Math.round(rate * 10) / 10,
    conv: Math.round((sent * rate) / 100),
    label: `${dt.getMonth() + 1}/${dt.getDate()}`,
  };
});

const RC_D90: RecallChartPoint[] = Array.from({ length: 13 }, (_, i) => {
  const dt = new Date(RC_TODAY);
  dt.setDate(dt.getDate() - (12 - i) * 7);
  const sent = Math.round(5180 + 360 * Math.sin(i / 2) + i * 34);
  const rate = 69 + i * 0.52;
  return {
    sent,
    rate: Math.round(rate * 10) / 10,
    conv: Math.round((sent * rate) / 100),
    label: `${RC_MONTHS[dt.getMonth()]} ${dt.getDate()}`,
  };
});

const RC_D12: RecallChartPoint[] = [
  { m: 6, sent: 17800, rate: 62 },
  { m: 7, sent: 18600, rate: 63 },
  { m: 8, sent: 19400, rate: 64 },
  { m: 9, sent: 21050, rate: 61 },
  { m: 10, sent: 20200, rate: 66 },
  { m: 11, sent: 22800, rate: 67 },
  { m: 0, sent: 23950, rate: 68 },
  { m: 1, sent: 24700, rate: 70 },
  { m: 2, sent: 25600, rate: 72 },
  { m: 3, sent: 24100, rate: 71 },
  { m: 4, sent: 25900, rate: 73 },
  { m: 5, sent: 26400, rate: 73 },
].map((d) => ({
  sent: d.sent,
  rate: d.rate,
  conv: Math.round((d.sent * d.rate) / 100),
  label: RC_MONTHS[d.m]!,
}));

export const RC_CHART: Record<Exclude<RecallRangeKey, 'empty'>, RecallChartPoint[]> = {
  '7d': RC_D7,
  '30d': RC_D30,
  '90d': RC_D90,
  '12m': RC_D12,
};

export const RC_CHART_META: Record<
  Exclude<RecallRangeKey, 'empty'>,
  { title: string; sub: string }
> = {
  '7d': { title: 'Daily volume', sub: 'Messages sent vs. converted each day, with conversion-rate overlay' },
  '30d': { title: 'Conversion trend', sub: 'Daily converted patients across the last 30 days' },
  '90d': { title: 'Weekly performance', sub: 'Converted patients per week, with conversion-rate trend' },
  '12m': { title: 'Monthly performance', sub: 'Sent vs. converted by month, with conversion-rate trend' },
};

export const RC_TONE_COLORS: Record<string, string> = {
  info: 'var(--info)',
  ai: 'var(--ai-3)',
  lav: '#7A4FD6',
  success: 'var(--success)',
  warning: 'var(--terracotta)',
  gold: 'var(--gold)',
};

export const RC_TONE_TINTS: Record<string, string> = {
  info: 'var(--info-tint)',
  ai: 'var(--ai-tint)',
  lav: 'rgba(122,79,214,0.12)',
  success: 'var(--success-tint)',
  warning: 'var(--terracotta-tint)',
  gold: 'var(--gold-tint)',
};
